import { RootState } from 'types/state';
import React from 'react';
import { connect } from 'react-redux';
import { i18n } from 'config/locales';
import { CommonOnUpdate, CommonOnError } from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import classnames from 'classnames';
import { FeatureFlag } from 'components/FeatureFlags';
import ErrorMessage from 'components/ErrorMessage';
import { fetchInstitutionSearchRequest } from '@redux/modules/api/actions';
import { debounce } from 'lodash';
import ValidationElement from '../ValidationElement';
import Show from '../Show';
import Dropdown from '../Dropdown';
import { internalTesting } from '../Dropdown/Dropdown';
import MultipleDropdown from '../MultipleDropdown';
import { ValidationElementProps } from '../ValidationElement/ValidationElement';

interface Props extends ValidationElementProps {
  showComments?: boolean
  attribute?: string
  name: string
  label?: any
  placeholder?: any
  ariaLabel?: string
  disabled?: any
  required?: boolean
  value?: string | string[]
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  className?: string
  requireUnitedStates?: boolean
  fetchInstitutions?: (findText: string) =>any
  institutionsList?: object
}

interface State {
  showComments: boolean
  focus: boolean
}

class InstitutionAutoComplete extends ValidationElement<Props, State> {
  // This is different, as institution has a stateFormErrorConnector and it is
  // messing with the type.
  institution: React.RefObject<typeof internalTesting.Dropdown>;

  constructor(props: Props) {
    super(props);

    this.state = {
      showComments: props.showComments,
      focus: false,
    };

    this.institution = React.createRef();
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.onSuggestionSelected = debounce(this.onSuggestionSelected, 500, { leading: false, trailing: true });
    this.fetchSuggestions = this.fetchSuggestions.bind(this);
    this.fetchSuggestions = debounce(this.fetchSuggestions, 700, { leading: false, trailing: true });
  }

  update = (queue, institutions) => {
    const newQueue = { ...queue };
    if (newQueue.value && newQueue.value !== this.props.value) {
      const hasCode = newQueue.value?.match(/ - \d\d\d\d\d\d+$/);
      const hasMatch = institutions?.Any?.((x) => x.value === newQueue.value);
      if (hasCode && !hasMatch) {
        newQueue.value = newQueue.value.replace(/ - \d\d\d\d\d\d+$/, '');
      }
    }

    this.props.onUpdate({
      name: this.props.name,
      showComments: this.props.showComments,
      value: this.props.value,
      focus: this.state.focus,
      ...newQueue,
    });
  };

  fetchSuggestions(value) {
    const valueObj = value;
    if (!valueObj?.length) {
      return;
    }
    let findText = '';
    if (Array.isArray(valueObj)) {
      findText = valueObj[0];
    } else {
      findText = valueObj;
    }
    // Still search if they have a code visible but are changing the text in case they misclicked on something.
    findText = findText.replace(/ - \d+$/i, '');
    // Here are the different checks before sending a fetch operation out
    // Minimum of 3 characters to search
    if (findText?.length >= 3) {
      this.props.fetchInstitutions?.(findText);
    }
  }

  /**
   * Update function when there is only one institution
   * @param {object} values - Institution object
   */
  updateSingleInstitution = (values) => {
    const { value } = values;

    if (value != this.props.value) {
      this.fetchSuggestions(value);
    }
    this.update({ value }, this.getInstitutionsList());
  };

  /**
   * Filters institution names match anything in the institution list
   * @param {string} item - Institution Name
   */
  filterValidInstitutions = (item) => {
    const institutions = this.getInstitutionsList() || [];
    return institutions.filter((x) => (x.value?.toLowerCase() === item.toLowerCase()));
  };

  handleError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      code: `institution.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    }));

    // Determine if a `notfound` error is present and has a value of `false`.
    // When this happens we want to change the focus to the appropriate element
    // dependent on if this is singular or plural.
    const notfound = arr2.some((x) => x.valid === false && x.code.includes('notfound'));

    this.setState({ showComments: notfound });

    return this.props.onError(value, arr2);
  };

  /**
   * Handle the focus event.
   */
  handleFocus = (event) => {
    this.setState({ focus: true }, () => {
      this.props.updateInstitutionFocused(this.state.focus);
      super.handleFocus(event);
    });
  };

  /**
   * Handle the blur event.
   */
  handleBlur = (event) => {
    this.setState({ focus: false }, () => {
      this.props.updateInstitutionFocused(this.state.focus);
      super.handleBlur(event);
    });
  };

  validInstitution(value): boolean {
    const institutions = this.getInstitutionsList() || [];
    const options = institutions?.map((x) => x.value);
    return options.includes(value);
  }

  getInstitutionsList = () => {
    let institutions: Array<{ institutionName: string, code1?: string, city?: string, statecode?: string, country?: string, street1?:string, street2?:string, zipcode?:string, id:number, displayText?:string, value?:string }> = this.props.institutionsList?.institutions ?? []; // eslint-disable-line max-len
    institutions = institutions.map((x) => {
      const city = x.city || '';
      const statecode = x.statecode || '';
      const displayText = `${x.institutionName} - ${x.city}, ${x.statecode}`;
      const value = x.code1 ? `${x.institutionName} - ${x.code1}` : `${x.institutionName}`;
      return { ...x, displayText, value };
    });
    return institutions;
  };

  renderOptions = () => {
    const filter = this.props.requireUnitedStates
      ? (x) => (x.country === 'USA' || x.country === 'United States')
      : () => true;

    const institutions = this.getInstitutionsList();
    // possibly readd key into the option below taking it out for some inition testing check country for a variant
    const institutionOptions = institutions.filter(filter)
      .map((x) => (
        <option key={x.id} value={`${x.value || ''}`}>
          {`${x.displayText || ''}`}
        </option>
      ));

    // Check for children
    const children = this.props.children || [];
    const options = institutionOptions.concat(
      children.map((x) => {
        if (x && x.type === 'option') {
          return x;
        }
        return null;
      })
    );

    if (this.props.placeholder) {
      return [
        <option key="placeholder" value="">
          {this.props.placeholder}
        </option>,
      ].concat(options);
    }

    return options;
  };

  appropriateValue = (value: Props['value']) => {
    if (!value) {
      return '';
    }

    // For the typical Dropdown component a string value is expected.
    // However, for the MultiDropdown, the value must be an array of objects.
    let simpleValue;
    const isArray = Array.isArray(value);
    if (isArray) {
      simpleValue = value[0];
    } else {
      simpleValue = value;
    }

    return simpleValue;
  };

  onSuggestionSelected(event, options) {
    const allInstitutions = this.getInstitutionsList();
    const selectedInstitution = allInstitutions.find((x) => options?.suggestion?.text === x.displayText);
    if (this.props.onSuggestionSelected) {
      this.props.onSuggestionSelected(event, options, selectedInstitution);
    }
  }

  render() {
    const className = classnames(['institution', 'usa-form-control'], this.props.className);
    const options = this.renderOptions();
    const value = this.appropriateValue(this.props.value);
    return (
      <div>
        <div className="institue-message">
          <b style={{ fontSize: 'larger' }}>&#x24d8;</b>  As you type the name of the school, a list of matching US schools
          from the US Department of Education will appear.  If you choose a matching school from the list, the name will
          be followed by the code for that school, if available.  The address on file at the Department of Education will
          be filled in automatically.  Please ensure the address is correct.  If the address is not correct, add the
          correct address. Any changes to the school name field will clear all fields in the address until a selection
          from the dropdown is made.  For correspondence/distance/extension/online schools, provide the address where
          the records are maintained.
        </div>
        {this.state.focus && value && !this.validInstitution(value) && (
          <ErrorMessage
            isWarning
            title="Please select from the list, if possible. Top 100 matching results will be shown."
          />
        )}
        <Dropdown
          attribute={this.props.attribute}
          name={this.props.name}
          ref={this.institution}
          label={this.props.label}
          placeholder={this.props.placeholder}
          className={className}
          ariaLabel={this.props.ariaLabel}
          disabled={this.props.disabled}
          onUpdate={this.updateSingleInstitution}
          onError={this.handleError}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onSuggestionSelected={this.onSuggestionSelected}
          value={value}
          required={this.props.required}
          minSuggestionLength={3}
          suggestions={options}
          sort={false}
          suggestionsRenderProvided
          dynamicSuggestions
        >
          {options}
        </Dropdown>

        {/* TODO: Featureflag Item needed? */}
      </div>
    );
  }
}

InstitutionAutoComplete.defaultProps = {
  name: 'institution',
  value: [],
  focus: false,
  showComments: false,
  requireUnitedStates: true,
  updateInstitutionFocused: (focus) => { },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  onSuggestionSelected: (event, options, selectedInstitution) => {}
};

InstitutionAutoComplete.errors = [];

const mapStateToProps = (state:RootState) => {
  const institutionsList = state.api.fetchInstitutionSearch?.response || {};
  return {
    institutionsList,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchInstitutions: (findText:string) => dispatch(fetchInstitutionSearchRequest(findText)),
});

export { InstitutionAutoComplete };
export default connect(mapStateToProps, mapDispatchToProps)(InstitutionAutoComplete);
